import React, { useEffect, useState } from 'react';
import './Reports.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import ExcelJS from 'exceljs';
import Modal from './alertModal';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import format from 'date-fns/format';
import { toast } from 'react-toastify';
import { Spinner, Alert } from 'react-bootstrap';
import { formatISOToLocalTime, normalizeDbTimestampToUtcIso } from '../../utils/timeUtils';

const InfoTable = ({ adminIds, empRole }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDateError, setShowDateError] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  useEffect(() => {
    const detectTimezone = () => {
      const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedZone !== userTimeZone) {
        setUserTimeZone(detectedZone);
      }
    };

    window.addEventListener('focus', detectTimezone);

    return () => {
      window.removeEventListener('focus', detectTimezone);
    };
  }, [userTimeZone]);

  useEffect(() => {
    setLoading(true);
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get('/admin/employees');
        let filteredEmployees = empRole === 'SuperAdmin'
          ? response.data
          : response.data.filter(employee => adminIds.includes(employee.emp_id));
        setEmployees(filteredEmployees);
      } catch (error) {
        if (!error.response) {
          toast.error("Looks like you are offline. Please check your internet connectivity.");
        } else {
          console.error('Error fetching employees:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [adminIds, empRole]);

  const handleExport = async (employeeId, employeeName) => {
    if (!fromDate || !toDate) {
      setShowModal(true);
      return;
    }

    const fromDateObj = new Date(fromDate);
    const toDateObj = new Date(toDate);
    if (toDateObj < fromDateObj) {
      setShowDateError(true);
      return;
    } else {
      setShowDateError(false);
    }

    try {
      const response = await axiosInstance.get(`/admin/Employeeattendance/${employeeId}`, {
        params: { fromDate, toDate }
      });

      // Normalize the time data
      const normalizedData = response.data.map(entry => ({
        ...entry,
        punch_in_time: normalizeDbTimestampToUtcIso(entry.punch_in_time, entry.attendance_date),
        punch_out_time: normalizeDbTimestampToUtcIso(entry.punch_out_time, entry.attendance_date),
      }));

      exportToExcel(normalizedData, employeeName);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      console.error('Error details:', error.response || error.message);
    }
  };

  // Format timezone for display
  const formatTimeZone = () => {
    try {
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: userTimeZone
      }).formatToParts().find(part => part.type === 'timeZoneName')?.value;

      return timeZoneAbbr || userTimeZone.split('/').pop();
    } catch (e) {
      return userTimeZone.split('/').pop();
    }
  };

  const exportToExcel = (attendanceData, employeeName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Attendance', {
      views: [{ showGridLines: false }]
    });

    // Styling constants
    const headerStyle = {
      font: { bold: true, size: 12, color: { argb: '333333' }, name: 'Calibri' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } },
      alignment: { horizontal: 'center' },
      border: { bottom: { style: 'thin' } }
    };

    const dataStyle = {
      font: { size: 12, color: { argb: '000000' }, name: 'Calibri' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } },
      alignment: { horizontal: 'center' },
    };

    const totalRowStyle = {
      font: { bold: true, size: 12, color: { argb: 'FFFFFF' }, name: 'Calibri' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '000000' } },
      alignment: { horizontal: 'center' },
    };

    // Company header
    worksheet.mergeCells('C1:G1');
    worksheet.getCell('C1').value = 'HANSA-FLEX Pvt. Ltd.';
    worksheet.getCell('C1').font = { size: 16, bold: true };
    worksheet.getCell('C1').alignment = { horizontal: 'center' };
    worksheet.getCell('C1').fill = headerStyle.fill;
    worksheet.addRow([]);

    // Report title
    worksheet.mergeCells('C2:G2');
    worksheet.getCell('C2').value = t("reports_title");
    worksheet.getCell('C2').font = { size: 16, bold: true };
    worksheet.getCell('C2').alignment = { horizontal: 'center' };
    worksheet.getCell('C2').fill = headerStyle.fill;
    worksheet.addRow([]);

    // Add timezone info
    worksheet.mergeCells('C3:G3');
    // worksheet.getCell('C3').value = `All times shown in ${formatTimeZone()}`;
    worksheet.getCell('C3').font = { size: 12, italic: true };
    worksheet.getCell('C3').alignment = { horizontal: 'center' };
    worksheet.addRow([]);

    const addOutlinedRow = (label, value) => {
      const row = worksheet.addRow(['', '', label, value]);
      ['C', 'D'].forEach(col => {
        const cell = row.getCell(col);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    };

    addOutlinedRow(t("empForm_namePlace"), employeeName);
    addOutlinedRow(t("Downloaded_on"), format(new Date(), 'dd/MM/yyyy'));
    addOutlinedRow(t("leaveRequest_startDate"), format(fromDate, 'dd/MM/yyyy'));
    addOutlinedRow(t("leaveRequest_endDate"), format(toDate, 'dd/MM/yyyy'));
    worksheet.addRow([]);

    // Headers
    const headers = [t("employeeWorkDetails_date"), t("attendance_login"), t("attendance_logout"), t("reports_excelBreak"), t("reports_excelAttendance")];
    const headerRow = worksheet.addRow(['', '', ...headers]);
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber > 2) {
        Object.assign(cell, headerStyle);
      }
    });

    // Data rows
    let absences = 0;
    let presences = 0;

    attendanceData.forEach((entry, index) => {
      const row = worksheet.addRow([
        '', '',
        format(entry.attendance_date, 'dd/MM/yyyy'),
        entry.punch_in_time ? formatISOToLocalTime(entry.punch_in_time) : '-',
        entry.punch_out_time ? formatISOToLocalTime(entry.punch_out_time) : '-',
        entry.break_time || '-',
        formatLeaveType(entry.attendance) || '-'
      ]);

      row.eachCell((cell, colNumber) => {
        if (colNumber > 2) {
          Object.assign(cell, dataStyle);
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: index % 2 === 0 ? 'FFFFFF' : 'F0F0F0' }
          };
        }
      });

      if (entry.attendance === 'a') absences++;
      else if (entry.attendance === 'p') presences++;
    });

    // Totals
    worksheet.addRow([]);
    [
      [t("reports_absentese"), absences],
      [t("reports_precentese"), presences]
    ].forEach(([label, value]) => {
      const row = worksheet.addRow(['', '', label, value]);
      row.eachCell((cell, colNumber) => {
        if (colNumber > 2) {
          Object.assign(cell, totalRowStyle);
        }
      });
    });

    // Column widths
    worksheet.columns.forEach(column => {
      column.width = 20;
    });

    // Generate and download
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${employeeName}_Attendance.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const formatLeaveType = (string) => {
    if (!string) return '';
    const words = string.split('_');
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="info-table-container">
      <h2 className='pgTitle'>{t("reports_title")}</h2>
      {/* <div className="timezone-info">
        ({t("Times shown in")} {formatTimeZone()})
      </div> */}

      <Modal show={showModal} handleClose={() => setShowModal(false)} message={t("reports_selectDate")} />
      {showDateError && <Modal show={true} handleClose={() => setShowDateError(false)} message={t("reports_datePrecede")} />}

      <div className="datepicker-container">
        <label htmlFor="fromDate" style={{ fontSize: '12px', fontWeight: '500' }}>{t("from")}:</label>
        <input type="date" id="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <label htmlFor="toDate" style={{ fontSize: '12px', fontWeight: '500' }}>{t("to")}:</label>
        <input type="date" id="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>{t("projectDetails_EmployeeID")}</th>
              <th>{t("attendance_name")}</th>
              <th>{t("reports_title")}</th>
            </tr>
          </thead>
          <tbody style={{ background: '#fff' }}>
            {loading ? (
              <tr>
                <td className='table-data-row'></td>
                <td colSpan='2' className="employee-table-Spinner centered">
                  <Spinner animation="border" role="status">
                    <span className=""></span>
                  </Spinner>
                  <span className="mt-2">{t('Please wait your data is loading')}</span>
                </td>
                <td className='table-data-row'></td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td></td>
                <td className="centered">
                  <Alert variant="light" className='employee-table-Alert'>
                    {t("No employees found")}
                  </Alert>
                </td>
                <td></td>
              </tr>
            ) : employees.map(employee => (
              <tr key={employee.emp_id}>
                <td>{employee.emp_sap_id}</td>
                <td>{employee.p_name}</td>
                <td>
                  <button className='download-btn' onClick={() => handleExport(employee.emp_id, employee.p_name)}>
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InfoTable;